import { v2Route } from "./v2";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import RootLayout from "@/layouts/RootLayout";
import AppLayout from "@/layouts/AppLayout";
import AuthLayout from "@/layouts/AuthLayout";
import ExportLayout from "@/layouts/ExportLayout";
import { AuthGuard, GuestGuard } from "@/features/auth/components/AuthGuard";

// Lazy load pages for code splitting
const LoginPage = lazy(() => import("@/pages/login"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const DashboardPage = lazy(() => import("@/pages/dashboard"));

// Master Data
const ClientPage = lazy(() => import("@/pages/master-data/client"));
const VendorPage = lazy(() => import("@/pages/master-data/vendor"));
const UserPage = lazy(() => import("@/pages/master-data/user"));
const AccountPage = lazy(() => import("@/pages/master-data/account"));
const ContactPage = lazy(() => import("@/pages/master-data/contact"));
const CustomerPage = lazy(() => import("@/pages/master-data/customer"));

// Settings
const GeneralSettingsPage = lazy(() => import("@/pages/settings/general"));
const PaymentMethodsPage = lazy(
  () => import("@/pages/settings/payment-methods"),
);
const RolesPage = lazy(() => import("@/pages/settings/roles"));

// Financial
const TransactionPage = lazy(() => import("@/pages/financial/transaction"));
const BillPage = lazy(() => import("@/pages/financial/bill"));
const AccountReceivablePage = lazy(
  () => import("@/pages/financial/account-receivable"),
);
const AccountPayablePage = lazy(
  () => import("@/pages/financial/account-payable"),
);
const InvoiceListPage = lazy(() => import("@/pages/financial/invoice"));
const InvoiceDetailPage = lazy(() => import("@/pages/financial/invoice/[id]"));
const InvoiceCreatePage = lazy(
  () => import("@/pages/financial/invoice/create"),
);
const JournalPage = lazy(() => import("@/pages/financial/journal"));
const JournalNewPage = lazy(() => import("@/pages/financial/journal/new"));
const JournalEditPage = lazy(
  () => import("@/pages/financial/journal/[id]/edit"),
);
const PettyCashPage = lazy(() => import("@/pages/financial/petty-cash"));

// Export
const ExportInvoicePage = lazy(() => import("@/pages/export/invoice/[id]"));
const ExportBillPage = lazy(() => import("@/pages/export/bill/[id]"));
const ExportArPage = lazy(() => import("@/pages/export/ar/[id]"));
const ExportApPage = lazy(() => import("@/pages/export/ap/[id]"));

// Loading component for Suspense
function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// Wrapper for lazy components
function LazyPage({
  Component,
}: {
  Component: React.LazyExoticComponent<() => React.ReactElement>;
}) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Vite replaces this built-in flag and eliminates the exploration import in production.
  // eslint-disable-next-line turbo/no-undeclared-env-vars
  ...(import.meta.env.DEV
    ? [
        {
          path: "/exploration/financial-workbench",
          lazy: async () => {
            const { default: Component } =
              await import("@/pages/exploration/financial-workbench");
            return { Component };
          },
        },
        {
          path: "/exploration/travallis-ledger",
          lazy: async () => {
            const { default: Component } =
              await import("@/pages/exploration/travallis-ledger");
            return { Component };
          },
        },
      ]
    : []),
  {
    path: "/",
    element: <RootLayout />,
    children: [
      v2Route,
      // Auth routes (guest only - redirect to dashboard if authenticated)
      {
        element: <GuestGuard />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: "login",
                element: <LazyPage Component={LoginPage} />,
              },
              {
                path: "reset-password/:token",
                element: <LazyPage Component={ResetPasswordPage} />,
              },
            ],
          },
        ],
      },
      // Protected app routes (require authentication)
      {
        element: <AuthGuard />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                element: <Navigate to="/dashboard" replace />,
              },
              {
                path: "dashboard",
                element: <LazyPage Component={DashboardPage} />,
              },
              // Master Data
              {
                path: "master-data",
                children: [
                  {
                    path: "client",
                    element: <LazyPage Component={ClientPage} />,
                  },
                  {
                    path: "vendor",
                    element: <LazyPage Component={VendorPage} />,
                  },
                  {
                    path: "user",
                    element: <LazyPage Component={UserPage} />,
                  },
                  {
                    path: "account",
                    element: <LazyPage Component={AccountPage} />,
                  },
                  {
                    path: "contact",
                    element: <LazyPage Component={ContactPage} />,
                  },
                  {
                    path: "customer",
                    element: <LazyPage Component={CustomerPage} />,
                  },
                ],
              },
              // Settings
              {
                path: "settings",
                children: [
                  {
                    path: "general",
                    element: <LazyPage Component={GeneralSettingsPage} />,
                  },
                  {
                    path: "payment-methods",
                    element: <LazyPage Component={PaymentMethodsPage} />,
                  },
                  {
                    path: "roles",
                    element: <LazyPage Component={RolesPage} />,
                  },
                ],
              },
              // Financial
              {
                path: "financial",
                children: [
                  {
                    path: "transaction",
                    element: <LazyPage Component={TransactionPage} />,
                  },
                  {
                    path: "bill",
                    element: <LazyPage Component={BillPage} />,
                  },
                  {
                    path: "account-receivable",
                    element: <LazyPage Component={AccountReceivablePage} />,
                  },
                  {
                    path: "account-payable",
                    element: <LazyPage Component={AccountPayablePage} />,
                  },
                  {
                    path: "invoice",
                    element: <LazyPage Component={InvoiceListPage} />,
                  },
                  {
                    path: "invoice/create",
                    element: <LazyPage Component={InvoiceCreatePage} />,
                  },
                  {
                    path: "invoice/:id",
                    element: <LazyPage Component={InvoiceDetailPage} />,
                  },
                  {
                    path: "journal",
                    element: <LazyPage Component={JournalPage} />,
                  },
                  {
                    path: "journal/new",
                    element: <LazyPage Component={JournalNewPage} />,
                  },
                  {
                    path: "journal/:id/edit",
                    element: <LazyPage Component={JournalEditPage} />,
                  },
                  {
                    path: "petty-cash",
                    element: <LazyPage Component={PettyCashPage} />,
                  },
                ],
              },
            ],
          },
        ],
      },
      // Export routes (no sidebar, but still protected)
      {
        element: <AuthGuard />,
        children: [
          {
            element: <ExportLayout />,
            children: [
              {
                path: "export/invoice/:id",
                element: <LazyPage Component={ExportInvoicePage} />,
              },
              {
                path: "export/bill/:id",
                element: <LazyPage Component={ExportBillPage} />,
              },
              {
                path: "export/ar/:id",
                element: <LazyPage Component={ExportArPage} />,
              },
              {
                path: "export/ap/:id",
                element: <LazyPage Component={ExportApPage} />,
              },
            ],
          },
        ],
      },
    ],
  },
]);
